import { Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "shared/hooks/useTranslation";
import MoneyInput from "shared/ui/MoneyInput";

interface SplitPerson {
    id: string;
    label: string;
    included: boolean;
    value: string;
    readOnly: boolean;
}

interface SplitBetweenSectionProps {
    people: SplitPerson[];
    onToggle: (personId: string, checked: boolean) => void;
    onValueChange: (personId: string, value: string) => void;
    disabled?: boolean;
}

function dedupeSplitPeople(people: SplitPerson[]): SplitPerson[] {
    const byId = new Map<string, SplitPerson>();
    for (const person of people) {
        const id = String(person.id ?? "").trim();
        if (!id) continue;
        const current = byId.get(id);
        if (!current) {
            byId.set(id, { ...person, id });
            continue;
        }
        byId.set(id, {
            ...current,
            ...person,
            id,
            label: person.label || current.label,
            included: current.included || person.included,
            value: person.value || current.value,
        });
    }
    return [...byId.values()];
}

export default function SplitBetweenSection({ people, onToggle, onValueChange, disabled }: SplitBetweenSectionProps) {
    const { t } = useTranslation();
    const visiblePeople = useMemo(() => dedupeSplitPeople(people), [people]);

    return (
        <>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{t("forms.splitBetween")}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {t("forms.splitBetweenHint")}
            </Typography>
            {visiblePeople.map((person) => (
                <Stack key={person.id} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={person.included}
                                onChange={(e) => onToggle(person.id, e.target.checked)}
                                size="small"
                                disabled={disabled}
                            />
                        }
                        label={person.label}
                        sx={{ flex: 1, mr: 0 }}
                    />
                    <MoneyInput
                        size="small"
                        value={person.value}
                        onValueChange={(raw) => onValueChange(person.id, raw)}
                        InputProps={{ readOnly: person.readOnly }}
                        disabled={disabled}
                        sx={{ width: "60%" }}
                    />
                </Stack>
            ))}
        </>
    );
}
